import {
  AppLayout,
  Box,
  Button,
  Container,
  Flashbar,
  FormField,
  Grid,
  Header,
  HelpPanel,
  Link,
  Multiselect,
  SegmentedControl,
  SpaceBetween,
  SplitPanel,
  Tabs
} from '@amzn/awsui-components-core';
import Select from '@amzn/awsui-components-core/select';
import React, { useEffect, useMemo, useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { ErrorContainer } from 'src/components/fintech-ops/FinTechOpsCommonElements';
import {
  BEST_PRACTICES_URL,
  DURATION_DATA,
  L8_MANAGER_DETAILS,
  RISK_REMEDIATOR_API_ERROR,
  RISK_TYPE,
  RISK_TYPE_FLAG,
  SPLIT_PANEL_CONFIG,
  USER_GUIDE_URL
} from 'src/components/fintech-ops/risk-remediator/constants';
import styles from 'src/components/fintech-ops/risk-remediator/styles.module.scss';
import {
  fetchRiskRemediatorRisks,
  filterByClassification,
  filterDatabyRiskCategory,
  filterDataByType
} from 'src/components/fintech-ops/risk-remediator/utils';

import LastRefreshedAt from 'src/components/generic-components/LastRefreshedAt';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { splitPanelI18nStrings } from 'src/i18n-strings';
import { getMultiSelectPlaceHolderValue } from 'src/utilities/CommonUtilities';
import { DFPHomePageSideNavigation } from '../../dfp-home-page/DFPHomePageSideNavigation';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { RiskDetailsTable } from './RiskDetailsTable';
import { RiskRemediatorOverview } from './RiskRemediatorOverviewPage';
import {
  asrColumnDef,
  getTableColDefs,
  RISK_DETAILS_VISIBLE_CONTENT,
  RISK_REMEDIATOR_ASR_FILTERING_PROPERTIES,
  RISK_REMEDIATOR_DETAILS_FILTERING_PROPERTIES,
  RISK_REMEDIATOR_VPC_FILTERING_PROPERTIES,
  vpcColumnDef
} from './RiskRemediatorTableFilterConfig';
import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

export const RiskRemediatorMain: React.FC = () => {
  const finOpsContext = useFinTechContext();
  const [managersList, selectManagerList] = useState<any>([{ label: L8_MANAGER_DETAILS.name, value: L8_MANAGER_DETAILS.login }]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isChartSummaryLoading, setIsChartSummaryLoading] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [managerSelector, selectManagerSelector] = useState<any>({ label: L8_MANAGER_DETAILS.name, value: L8_MANAGER_DETAILS.login });
  const [durationSelector, setDurationSelector] = useState<any>({ label: DURATION_DATA[0]['label'], value: DURATION_DATA[0]['value'] });
  const [riskTypeSelector, setRiskTypeSelector] = useState<any>([
    { label: RISK_TYPE[0]['options'][0]['label'], value: RISK_TYPE[0]['options'][0]['value'] },
    { label: RISK_TYPE[0]['options'][1]['label'], value: RISK_TYPE[0]['options'][1]['value'] }
  ]);
  const [flashMsgStatus, setFlashMsgStatus] = useState<any>([]);
  const [remediatedRiskData, setRemediatedRiskData] = useState<any>([]);
  const [devRemediatedRiskData, setDevRemediatedRiskData] = useState<any>([]);
  const [checked, setChecked] = useState<string>('auto');
  const [classificationChecked, setClassificationChecked] = useState<string>('Violation');
  const [openRiskData, setOpenRiskData] = useState<any>([]);
  const [refreshedTime, setRefreshedTime] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isResetFilter, setIsResetFilter] = useState<boolean>(false);
  const [riskRemediatorData, setRiskRemediatorData] = useState<any>('');
  const [directsData, setDirectsData] = useState<string>('');
  const [toolsOpen, setToolsOpen] = useState<any>(false);
  const [asrData, setAsrData] = useState<any>([{}]);
  const [vpcData, setVpcData] = useState<any>([{}]);
  const [selectedItem, setSelectedItem] = useState<any>();
  const [splitPanelOpen, setSplitOpen] = useState<any>(false);
  const [splitPanelSize, setSplitPanelSize] = useState<any>(SPLIT_PANEL_CONFIG.panelWidth);
  const [hasManuallyClosedOnce, setHasManuallyClosedOnce] = useState(false);
  const [panelHeader, setPanelHeader] = useState<any>(SPLIT_PANEL_CONFIG.EMPTY_PANEL_CONTENT.header);
  const [panelBody, setPanelBody] = useState<any>(SPLIT_PANEL_CONFIG.EMPTY_PANEL_CONTENT.body);
  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  const isSplitToggleEnabled = (isSplitPanelOpen: boolean, header: any, body: any) => {
    setSplitOpen(isSplitPanelOpen);

    setPanelBody(body);
    setPanelHeader(header);
    if (isSplitPanelOpen) {
      setToolsOpen(false);
    }
  };

  // Calculate summary filters directly - no extra renders
  const currentSummaryFilters = useMemo(() => {
    const allFilters = {
      manager: managerSelector,
      duration: durationSelector,
      riskType: riskTypeSelector
    };
    return JSON.stringify(allFilters);
  }, [managerSelector, durationSelector, riskTypeSelector]);

  const props = {
    openRiskData,
    remediatedRiskData,
    devRemediatedRiskData,
    refreshedTime,
    selectedManager: managerSelector,
    duration: durationSelector.value,
    isLoading,
    riskType: riskTypeSelector,
    directsData,
    asrData,
    vpcData,
    isSplitToggleEnabled,
    summaryFilters: currentSummaryFilters,
    originalFilterOptions
  };

  const flashBarErrorHandler = (errorMsg: string) => {
    setFlashMsgStatus([{ header: errorMsg, dismissible: true, type: 'error', onDismiss: () => setFlashMsgStatus([]) }]);
  };

  useEffect(() => {
    if (finOpsContext.finTechOpsRiskRemediatorStatus == 'finished') {
      selectManagerList(finOpsContext.finTechOpsRemediatorManagerData);
      setDirectsData(JSON.parse(finOpsContext.finTechOpsRemediatorDirectsData.details)[managerSelector.value] || '');
    }
  }, [managerSelector]);

  useEffect(() => {
    setLoading(true);
    if (finOpsContext.finTechOpsRiskRemediatorStatus == 'finished') {
      finOpsContext.setFinTechOpsRiskRemediatorStatus('loading');
      fetchRiskRemediatorRisks(durationSelector.value, L8_MANAGER_DETAILS.login).then((risks) => {
        setRefreshedTime(risks.refresh_time);
        setRiskRemediatorData(risks);
        finOpsContext.setFinTechOpsRiskRemediatorStatus('finished');
      });
    }
    setLoading(false);
  }, [durationSelector]);

  useEffect(() => {
    if (finOpsContext.finTechOpsRiskRemediatorStatus == 'error') {
      flashBarErrorHandler(RISK_REMEDIATOR_API_ERROR);
    }
  }, [finOpsContext.finTechOpsRiskRemediatorStatus]);

  useEffect(() => {
    if (finOpsContext.finTechOpsRiskRemediatorStatus !== 'finished') {
      return; // Exit early if data isn't ready
    }

    if (riskTypeSelector.length == 0) {
      setOpenRiskData([]);
      setRemediatedRiskData([]);
      setDevRemediatedRiskData([]);
      setAsrData([]);
      setVpcData([]);
    } else if (riskTypeSelector.length == 1) {
      setOpenRiskData(
        filterDatabyRiskCategory(filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG.OPEN, managerSelector.value), riskTypeSelector[0].value)
      );
      setRemediatedRiskData(
        filterDatabyRiskCategory(
          filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG['AUTO-REMEDIATED'], managerSelector.value),
          riskTypeSelector[0].value
        )
      );
      setDevRemediatedRiskData(
        filterDatabyRiskCategory(
          filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG['DEV-REMEDIATED'], managerSelector.value),
          riskTypeSelector[0].value
        )
      );
      if (finOpsContext.finTechOpsRemediatorAsrData.details !== undefined && finOpsContext.finTechOpsRemediatorAsrData.details != '') {
        setAsrData(
          filterDatabyRiskCategory(
            filterDataByType(finOpsContext.finTechOpsRemediatorAsrData.details, 'asr_details', managerSelector.value),
            riskTypeSelector[0].value
          )
        );
      }
      if (finOpsContext.finTechOpsRemediatorVpcData.details !== undefined && finOpsContext.finTechOpsRemediatorVpcData.details != '') {
        setVpcData(
          filterDatabyRiskCategory(
            filterDataByType(finOpsContext.finTechOpsRemediatorVpcData.details, 'vpc_details', managerSelector.value),
            riskTypeSelector[0].value
          )
        );
      }
    } else {
      setOpenRiskData(filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG.OPEN, managerSelector.value));
      setRemediatedRiskData(filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG['AUTO-REMEDIATED'], managerSelector.value));
      setDevRemediatedRiskData(filterDataByType(riskRemediatorData.details, RISK_TYPE_FLAG['DEV-REMEDIATED'], managerSelector.value));
      if (finOpsContext.finTechOpsRemediatorAsrData.details !== undefined && finOpsContext.finTechOpsRemediatorAsrData.details != '') {
        setAsrData(filterDataByType(finOpsContext.finTechOpsRemediatorAsrData.details, 'asr_details', managerSelector.value));
      }
      if (finOpsContext.finTechOpsRemediatorVpcData.details !== undefined && finOpsContext.finTechOpsRemediatorVpcData.details != '') {
        setVpcData(filterDataByType(finOpsContext.finTechOpsRemediatorVpcData.details, 'vpc_details', managerSelector.value));
      }
    }
  }, [riskTypeSelector, managerSelector, riskRemediatorData]);

  useEffect(() => {
    if (isResetFilter && finOpsContext.finTechOpsRiskRemediatorStatus == 'finished') {
      selectManagerSelector({ label: L8_MANAGER_DETAILS.name, value: L8_MANAGER_DETAILS.login });
      setDurationSelector({ label: DURATION_DATA[0]['label'], value: DURATION_DATA[0]['value'] });
      setRiskTypeSelector([
        { label: RISK_TYPE[0]['options'][0]['label'], value: RISK_TYPE[0]['options'][0]['value'] },
        { label: RISK_TYPE[0]['options'][1]['label'], value: RISK_TYPE[0]['options'][1]['value'] }
      ]);
      setRiskRemediatorData(finOpsContext.finTechOpsRiskRemediatorData);
      if (finOpsContext.finTechOpsRemediatorAsrData.details !== undefined && finOpsContext.finTechOpsRemediatorAsrData.details != '') {
        setAsrData(JSON.parse(finOpsContext.finTechOpsRemediatorAsrData.details).asr_details);
      }
      if (finOpsContext.finTechOpsRemediatorVpcData.details !== undefined && finOpsContext.finTechOpsRemediatorVpcData.details != '') {
        setVpcData(JSON.parse(finOpsContext.finTechOpsRemediatorVpcData.details).vpc_details);
      }
    }
    setIsResetFilter(false);
  }, [isResetFilter]);

  useEffect(() => {
    if (finOpsContext.finTechOpsRiskRemediatorStatus == 'finished') {
      // Capture original filter options on first data load (preserve true master filters)
      if (!originalFilterOptions) {
        const initialFilters = {
          manager: { label: L8_MANAGER_DETAILS.name, value: L8_MANAGER_DETAILS.login },
          duration: { label: DURATION_DATA[0]['label'], value: DURATION_DATA[0]['value'] },
          riskType: [
            { label: RISK_TYPE[0]['options'][0]['label'], value: RISK_TYPE[0]['options'][0]['value'] },
            { label: RISK_TYPE[0]['options'][1]['label'], value: RISK_TYPE[0]['options'][1]['value'] }
          ]
        };
        setOriginalFilterOptions(JSON.stringify(initialFilters));
      }

      if (riskRemediatorData === '') {
        setRiskRemediatorData(finOpsContext.finTechOpsRiskRemediatorData);
      }
      selectManagerList(finOpsContext.finTechOpsRemediatorManagerData);
      setDirectsData(JSON.parse(finOpsContext.finTechOpsRemediatorDirectsData.details)[managerSelector.value].replace('"', '') || '');
      if (finOpsContext.finTechOpsRemediatorAsrData.details !== undefined && finOpsContext.finTechOpsRemediatorAsrData.details != '') {
        setAsrData(JSON.parse(finOpsContext.finTechOpsRemediatorAsrData.details).asr_details);
      }
      if (finOpsContext.finTechOpsRemediatorVpcData.details !== undefined && finOpsContext.finTechOpsRemediatorVpcData.details != '') {
        setVpcData(JSON.parse(finOpsContext.finTechOpsRemediatorVpcData.details).vpc_details);
      }
      setLoading(false);
    }
  }, [
    finOpsContext.finTechOpsRiskRemediatorStatus,
    finOpsContext.finTechOpsRiskRemediatorData.details,
    finOpsContext.finTechOpsRemediatorAsrData.details,
    finOpsContext.finTechOpsRemediatorVpcData.details
  ]);

  const ManagerSelection = () => {
    return (
      <FormField label="Manager">
        <Select
          selectedOption={managerSelector}
          onChange={({ detail }) => selectManagerSelector(detail.selectedOption)}
          options={managersList}
          autoFocus
        />
      </FormField>
    );
  };

  const DurationSelection = () => {
    return (
      <FormField label="Duration">
        <Select
          selectedOption={durationSelector}
          onChange={({ detail }) => setDurationSelector(detail.selectedOption)}
          options={DURATION_DATA}
          autoFocus
        />
      </FormField>
    );
  };

  const RiskTypeMultiSelection = () => {
    return (
      <FormField label="Risk Type">
        <Multiselect
          selectedOptions={riskTypeSelector}
          onChange={({ detail }) => setRiskTypeSelector(detail.selectedOptions)}
          options={RISK_TYPE}
          placeholder={getMultiSelectPlaceHolderValue(riskTypeSelector, 'Risk Type', RISK_TYPE[0].options)}
          hideTokens
        />
      </FormField>
    );
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading: isChartSummaryLoading,
        setIsLoading: setIsChartSummaryLoading,
        isSplitPanelOpen: false,
        setIsSplitPanelOpen: () => {},
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        headerSelector="#h"
        navigation={<DFPHomePageSideNavigation />}
        notifications={<Flashbar items={flashMsgStatus} />}
        tools={
          <HelpPanel
            header={<h2>{Titles.TITLE_RISK_REMEDIATOR}</h2>}
            footer={
              <div>
                <Link external href={USER_GUIDE_URL}>
                  User Guide
                </Link>
                <br />
                <Link external href={BEST_PRACTICES_URL}>
                  DasFintech Best practices
                </Link>
              </div>
            }
          >
            <i>
              Risk Remediator is a tool to assess compliance status of AWS resources across all our AWS Accounts in real-time. Tool will remediate
              risks that doesnt need developer effort, else notify about the risks.
              <br />
              This tool does not fetch risks/metrics reported by other security portals and its not a replacement of currently used security tools
              like Shepherd,SAS,PolicyEngine,Dogma.
            </i>
          </HelpPanel>
        }
        onToolsChange={({ detail }) => {
          if (detail.open) {
            setSplitOpen(false);
          }
          setToolsOpen(detail.open);
        }}
        splitPanelPreferences={{ position: SPLIT_PANEL_CONFIG.position }}
        splitPanelOpen={splitPanelOpen}
        onSplitPanelToggle={({ detail }) => {
          if (detail.open) {
            setToolsOpen(false);
          }
          setSplitOpen(detail.open);
        }}
        splitPanelSize={splitPanelSize}
        onSplitPanelPreferencesChange={() => {}}
        onSplitPanelResize={(event) => setSplitPanelSize(Math.min(event.detail.size, SPLIT_PANEL_CONFIG.maxPanelWidth))}
        splitPanel={
          <SplitPanel i18nStrings={splitPanelI18nStrings} header={panelHeader}>
            {panelBody}
          </SplitPanel>
        }
        toolsOpen={toolsOpen}
        content={
          <>
            <Box>
              <SpaceBetween size="xxxs">
                <Container>
                  <SpaceBetween size="xxxs">
                    <SpaceBetween size="l" direction="horizontal" alignItems="center">
                      <Box variant="h2">{Titles.TITLE_RISK_REMEDIATOR}</Box>
                      <LastRefreshedAt lastRefreshedDateTime={refreshedTime} />
                      <Button onClick={() => setIsResetFilter(true)}>Clear Filters</Button>
                    </SpaceBetween>

                    <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
                      <ManagerSelection />
                      <DurationSelection />
                      <RiskTypeMultiSelection />
                    </Grid>
                  </SpaceBetween>
                </Container>
              </SpaceBetween>
            </Box>
            {finOpsContext.finTechOpsRiskRemediatorStatus == 'loading' && <LoadingSpinner />}
            {finOpsContext.finTechOpsRiskRemediatorStatus == 'error' && <ErrorContainer />}
            {finOpsContext.finTechOpsRiskRemediatorStatus == 'finished' && (
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'risk-remediator-OV',
                    content: <RiskRemediatorOverview {...props} />
                  },
                  {
                    label: 'Open Risks',
                    id: 'risk-remediator-OR',
                    content: (
                      <div>
                        <Container
                          fitHeight
                          header={
                            <Header
                              variant="h2"
                              actions={
                                <SegmentedControl
                                  selectedId={classificationChecked}
                                  onChange={({ detail }) => setClassificationChecked(detail.selectedId)}
                                  options={[
                                    { text: `Violations (${filterByClassification(props.openRiskData, 'Violation').length})`, id: 'Violation' },
                                    {
                                      text: `Recommendations (${filterByClassification(props.openRiskData, 'Recommendation').length})`,
                                      id: 'Recommendation'
                                    }
                                  ]}
                                />
                              }
                            >
                              Open Risks
                            </Header>
                          }
                        >
                          <div className={styles.riskDetailsTable}>
                            <RiskDetailsTable
                              pageSize={10}
                              emptyMessage="No Risk data to display"
                              data={filterByClassification(props.openRiskData, classificationChecked)}
                              filterProperties={RISK_REMEDIATOR_DETAILS_FILTERING_PROPERTIES}
                              visibleContent={RISK_DETAILS_VISIBLE_CONTENT}
                              columnDefinitions={getTableColDefs(true)}
                              headerName="Risk Details"
                              isLoading={props.isLoading}
                              isSplitToggleEnabled={props.isSplitToggleEnabled}
                              shouldEnableSelection={false}
                              defaultSortColumnKey={'event_timestamp'}
                              dataType={'Risks'}
                              enableDownloadReport={true}
                              downloadFileName="Open_Risk_Details"
                            />
                          </div>
                        </Container>
                      </div>
                    )
                  },
                  {
                    label: 'Remediated Risks',
                    id: 'risk-remediator-RR',
                    content: (
                      <div>
                        <Container
                          fitHeight
                          header={
                            <Header
                              variant="h2"
                              actions={
                                <SegmentedControl
                                  selectedId={checked}
                                  onChange={({ detail }) => setChecked(detail.selectedId)}
                                  options={[
                                    { text: `Auto Remediated (${props.remediatedRiskData.length})`, id: 'auto' },
                                    { text: `Manual Remediated (${props.devRemediatedRiskData.length})`, id: 'manual' }
                                  ]}
                                />
                              }
                            >
                              Remediated Risks
                            </Header>
                          }
                        >
                          <div className={styles.riskDetailsTable}>
                            <RiskDetailsTable
                              pageSize={10}
                              emptyMessage="No Risk data to display"
                              data={checked === 'manual' ? props.devRemediatedRiskData : props.remediatedRiskData}
                              filterProperties={RISK_REMEDIATOR_DETAILS_FILTERING_PROPERTIES}
                              visibleContent={RISK_DETAILS_VISIBLE_CONTENT}
                              columnDefinitions={getTableColDefs(false)}
                              headerName="Risk Details"
                              isLoading={props.isLoading}
                              isSplitToggleEnabled={props.isSplitToggleEnabled}
                              shouldEnableSelection={false}
                              defaultSortColumnKey={'event_timestamp'}
                              dataType={'Risks'}
                              enableDownloadReport={true}
                              downloadFileName={checked === 'manual' ? 'Manual_Remediated_Risk_Details' : 'Auto_Remediated_Risk_Details'}
                            />
                          </div>
                        </Container>
                      </div>
                    )
                  },
                  {
                    label: 'ASR Details',
                    id: 'risk-remediator-ASR',
                    content: (
                      <div>
                        <Container fitHeight>
                          <div className={styles.riskDetailsTable}>
                            <RiskDetailsTable
                              pageSize={10}
                              emptyMessage="No ASR data to display"
                              data={props.asrData}
                              filterProperties={RISK_REMEDIATOR_ASR_FILTERING_PROPERTIES}
                              columnDefinitions={asrColumnDef}
                              headerName="ASR Details"
                              isLoading={props.isLoading}
                              isSplitToggleEnabled={props.isSplitToggleEnabled}
                              shouldEnableSelection={true}
                              defaultSortColumnKey={'application_release_date'}
                              dataType={'Applications'}
                            />
                          </div>
                        </Container>
                      </div>
                    )
                  },
                  {
                    label: 'VPC Details',
                    id: 'risk-remediator-VPC',
                    content: (
                      <div>
                        <Container fitHeight>
                          <div className={styles.riskDetailsTable}>
                            <RiskDetailsTable
                              pageSize={10}
                              emptyMessage="No VPC data to display"
                              data={props.vpcData}
                              filterProperties={RISK_REMEDIATOR_VPC_FILTERING_PROPERTIES}
                              columnDefinitions={vpcColumnDef}
                              headerName="VPC Details"
                              isLoading={props.isLoading}
                              isSplitToggleEnabled={props.isSplitToggleEnabled}
                              shouldEnableSelection={false}
                              defaultSortColumnKey={'vpc_id'}
                              dataType={'vpc'}
                              enableDownloadReport={true}
                              downloadFileName="VPC_Details"
                            />
                          </div>
                        </Container>
                      </div>
                    )
                  }
                ]}
              />
            )}
          </>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
